import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BOOKING_DROP_OFF_LABELS,
  DEFAULT_JOURNEY_FUNNEL_PERIOD,
  JOURNEY_FUNNEL_PERIOD_DAYS,
  JOURNEY_PROGRESS_LABEL,
  ONBOARDING_WAITING_ON_LABEL,
  type BankIdPersons,
  type JourneyFunnel,
  type JourneyFunnelPeriod,
  type JourneyFunnelStepKey,
  type JourneyStepPatient,
  type OnboardingCohortReport,
  type OnboardingFunnelReport,
  type PatientGroup,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  fetchJourneyStepPatients,
  fetchOnboardingCohort,
  fetchOnboardingFunnel,
} from "@/lib/onboarding-funnel-api";
import {
  CONDITIONAL_STEP_NOTE,
  JOURNEY_PROGRESS_EXPLANATION,
  emptyJourneyStepText,
  JOURNEY_FILTERS,
  JOURNEY_PERIOD_FILTERS,
  journeyFunnelRows,
} from "@/lib/journey-funnel";
import { adminPatientHref } from "@/lib/patient-links";
import {
  bankIdMethodRows,
  bankIdPersonRows,
  ORIGIN_LABEL,
  ORIGIN_SOURCE_LABEL,
  segmentConversions,
  segmentNotes,
  segmentRows,
  COHORT_CHANNEL_LABEL,
  COHORT_STEP_LABEL,
  cohortBreakdownTables,
  cohortRows,
  F01_VERSION_BUCKET_LABEL,
  f01VersionRows,
  funnelRows,
  rangeEndingAt,
  rangeError,
  reminderFollowRows,
  reminderRows,
  stockholmToday,
  STEP_LABEL,
  type MeasuredNote,
} from "@/lib/onboarding-funnel";

// TES-1076 — Emelie's ten onboarding steps, from the first F-01 answers to a
// booked first care meeting, counted per Stockholm day. Counts only: the
// endpoint returns no patient, and this page shows none.
//
// TES-1096 — two views of the same period. "Aktivitet under perioden" is the
// per-day count above; "Kohortkonvertering" follows the people who STARTED
// in the period for seven days each, per start channel, and is the one to
// read the web → app → booking drop-off from.
//
// TES-1117 S3 — the cohort can be filtered to one journey (all, new
// treatment or transfer). With a journey, the report adds that
// journey's own funnel past the booking (for a transfer: F-02B, F-07,
// review, komplettering, decision, subscription, prescription), counted
// within the journey. Its counts stay de-identified; "Visa patienter" under
// a step asks the API for the people standing there, a separate admin-only
// call that logs every patient it returns (the TES-1109 contact list's
// rule). Ö-354 — that funnel has its own period (whose journey started in
// the last 7/30/90 days, or all; default 30) and splits the people at each
// step into ongoing and inactive 7+ days (Ö-355).

const fmt = new Intl.NumberFormat("sv-SE");

function MeasuredLine({ measured }: { measured: MeasuredNote | null }) {
  if (!measured) return null;
  return (
    <span
      className={`text-xs ${measured.afterStart ? "text-warning" : "text-textSecondary"}`}
      data-measured-since
    >
      {measured.text}
    </span>
  );
}

const PRESETS = [
  { days: 1, label: "Idag" },
  { days: 7, label: "7 dagar" },
  { days: 30, label: "30 dagar" },
] as const;

function FunnelTable({ report }: { report: OnboardingFunnelReport }) {
  const rows = funnelRows(report);
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Steg</th>
            <th className="px-md py-sm font-medium text-right">Antal</th>
            <th className="px-md py-sm font-medium text-right">
              Från föregående steg
            </th>
            <th className="px-md py-sm font-medium">Webb / app</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              className="border-t border-surfaceMuted align-top"
              data-step={r.key}
            >
              <td className="px-md py-sm">
                <div className="flex gap-sm">
                  <span className="text-textSecondary tabular-nums w-5 shrink-0">
                    {r.number}.
                  </span>
                  <div className="flex flex-col gap-xs min-w-0">
                    <span>{r.label}</span>
                    {r.note ? (
                      <span className="text-xs text-textSecondary max-w-md">
                        {r.note}
                      </span>
                    ) : null}
                    <MeasuredLine measured={r.measured} />
                  </div>
                </div>
              </td>
              <td className="px-md py-sm text-right tabular-nums w-40">
                <div className="flex items-center justify-end gap-sm">
                  <div
                    className="h-2 rounded-full bg-brand/40"
                    style={{ width: `${Math.max(4, (r.count / max) * 80)}px` }}
                    aria-hidden="true"
                  />
                  {fmt.format(r.count)}
                </div>
              </td>
              <td className="px-md py-sm text-right tabular-nums text-textSecondary">
                {r.fromPrevious === null ? "–" : `${r.fromPrevious} %`}
              </td>
              <td className="px-md py-sm text-textSecondary">
                {r.breakdown.length === 0
                  ? "–"
                  : r.breakdown
                      .map((b) => `${b.label} ${fmt.format(b.count)}`)
                      .join(" · ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// TES-1084 — the app-download reminder's effect, beside steps 7–8.
function ReminderSection({ report }: { report: OnboardingFunnelReport }) {
  const rows = reminderRows(report);
  const follow = reminderFollowRows(report);
  if (rows.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-sm"
      aria-labelledby="funnel-reminder-heading"
    >
      <h2 id="funnel-reminder-heading" className="text-sm font-medium">
        Påminnelsemejlet om appen (steg 7–8)
      </h2>
      <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto max-w-2xl">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className="border-t border-surfaceMuted first:border-t-0"
                data-reminder={r.key}
              >
                <td className="px-md py-sm">{r.label}</td>
                <td className="px-md py-sm text-right tabular-nums w-24">
                  {fmt.format(r.count)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-textSecondary max-w-2xl">
        Mejlet går ut ett dygn efter identifieringen om appen inte har öppnats,
        och avbryts om den öppnas med notisbehörighet först. Raderna ovan räknas
        den dag de hände, så ett mejl som schemalades dagen före perioden och
        skickades inom den räknas som skickat men inte som schemalagt – därför
        kan ”skickade” vara fler än ”schemalagda”. Inloggningen räknas per
        person: hennes första spår i appen efter mejlet. Sedan 2026-10-04 räknas
        ett klick även när mejlet öppnas i en mejlapp; före dess bara i en
        webbläsare där hon var inloggad på webben. Ett klick från en dator
        räknas inte, och högst ett klick per person och dag.
      </p>
      {follow.length > 0 ? (
        <>
          <h3 className="text-sm font-medium mt-sm">
            Samma personer: mejl schemalagda under perioden
          </h3>
          <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto max-w-2xl">
            <table className="w-full text-sm">
              <tbody>
                {follow.map((r) => (
                  <tr
                    key={r.key}
                    className="border-t border-surfaceMuted first:border-t-0"
                    data-reminder-follow={r.key}
                  >
                    <td className="px-md py-sm">{r.label}</td>
                    <td className="px-md py-sm text-right tabular-nums w-24">
                      {fmt.format(r.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-textSecondary max-w-2xl">
            ”Hade redan använt appen” är personer som öppnade appen utan att
            tillåta notiser innan mejlet gick – inget avbröt då mejlet, och det
            var inte mejlet som tog dem dit.
          </p>
        </>
      ) : null}
    </section>
  );
}

// TES-1096 — BankID by person; attempts only as a diagnostic.
function BankIdPersonsSection({
  bankId,
}: {
  bankId: BankIdPersons | undefined;
}) {
  const rows = bankIdPersonRows(bankId);
  if (rows.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-sm"
      aria-labelledby="funnel-bankid-persons-heading"
    >
      <h2 id="funnel-bankid-persons-heading" className="text-sm font-medium">
        BankID per person
      </h2>
      <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto max-w-2xl">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className="border-t border-surfaceMuted first:border-t-0"
                data-bankid-persons={r.key}
              >
                <td className="px-md py-sm">{r.label}</td>
                <td className="px-md py-sm text-right tabular-nums w-24">
                  {fmt.format(r.count)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-textSecondary max-w-2xl">
        Bara ett slutfört försök har en identitet, så en person kan räknas först
        när hon slutför. Försöken är diagnostik och används aldrig som underlag
        för en andel i tratten.
      </p>
    </section>
  );
}

// TES-1088 — how each BankID attempt was started, beside steps 2–3.
function BankIdMethodSection({ report }: { report: OnboardingFunnelReport }) {
  const rows = bankIdMethodRows(report);
  if (rows.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-sm"
      aria-labelledby="funnel-bankid-heading"
    >
      <h2 id="funnel-bankid-heading" className="text-sm font-medium">
        BankID per startsätt (steg 2–3)
      </h2>
      <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto max-w-2xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
              <th className="px-md py-sm font-medium">Startsätt</th>
              <th className="px-md py-sm font-medium text-right">Påbörjade</th>
              <th className="px-md py-sm font-medium text-right">Genomförda</th>
              <th className="px-md py-sm font-medium text-right">Andel</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className="border-t border-surfaceMuted"
                data-bankid-method={r.key}
              >
                <td className="px-md py-sm">{r.label}</td>
                <td className="px-md py-sm text-right tabular-nums">
                  {fmt.format(r.started)}
                </td>
                <td className="px-md py-sm text-right tabular-nums">
                  {fmt.format(r.completed)}
                </td>
                <td className="px-md py-sm text-right tabular-nums text-textSecondary">
                  {r.completedShare === null ? "–" : `${r.completedShare} %`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-textSecondary max-w-2xl">
        Varje försök räknas för sig, även återkommande inloggningar. På webben
        räknas ett försök som QR-kod tills hon trycker på ”Öppna BankID på denna
        enhet”.
      </p>
    </section>
  );
}

function DailyTable({ report }: { report: OnboardingFunnelReport }) {
  const keys = report.steps.map((s) => s.key);
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Dag</th>
            {keys.map((k, i) => (
              <th
                key={k}
                className="px-sm py-sm font-medium text-right"
                title={STEP_LABEL[k]}
                scope="col"
              >
                <abbr title={STEP_LABEL[k]} className="no-underline">
                  {i + 1}
                </abbr>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...report.daily].reverse().map((d) => (
            <tr key={d.day} className="border-t border-surfaceMuted">
              <td className="px-md py-sm whitespace-nowrap">{d.day}</td>
              {keys.map((k) => (
                <td key={k} className="px-sm py-sm text-right tabular-nums">
                  {fmt.format(d.counts[k] ?? 0)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The loaded report. Exported for the render test. */
// TES-1102 — why the people who started booking in the period did not book,
// as staff recorded it in Bokningsdiagnostik. Counts only, no person.
function DropOffReasonsSection({
  counts,
}: {
  counts: NonNullable<OnboardingFunnelReport["dropOffReasons"]>;
}) {
  const total = counts.reduce((n, c) => n + c.count, 0);
  return (
    <section className="flex flex-col gap-sm">
      <h2 className="font-display text-xl">Orsaker till avbruten bokning</h2>
      <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {counts.map((c) => (
              <tr
                key={c.category}
                className="border-t border-surfaceMuted first:border-t-0"
                data-drop-off={c.category}
              >
                <td className="px-md py-sm">
                  {BOOKING_DROP_OFF_LABELS[c.category]}
                </td>
                <td className="px-md py-sm text-right tabular-nums">
                  {fmt.format(c.count)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-textSecondary max-w-2xl">
        Bland dem som påbörjade bokningen under perioden och fick en orsak
        registrerad i Bokningsdiagnostik ({fmt.format(total)} st). Bara antal –
        anteckningar visas aldrig här.
      </p>
    </section>
  );
}

export function OnboardingFunnelView({
  report,
}: {
  report: OnboardingFunnelReport;
}) {
  const empty = report.steps.every((s) => s.count === 0);
  return (
    <div className="flex flex-col gap-lg">
      {empty ? (
        <div className="border border-surfaceMuted rounded-md p-lg bg-surface flex flex-col gap-sm items-start">
          <p className="text-sm font-medium">Ingen aktivitet under perioden</p>
          <p className="text-sm text-textSecondary max-w-lg">
            Siffrorna fylls på när någon svarar på frågorna, identifierar sig
            eller öppnar appen. Testkonton räknas inte.
          </p>
        </div>
      ) : null}

      <section className="flex flex-col gap-sm">
        <FunnelTable report={report} />
        <p className="text-xs text-textSecondary max-w-2xl">
          Varje person räknas den dag hon först nådde steget, så ett steg räknar
          varje person en gång. Andelen är steget delat med steget före under
          samma period – inte samma personer – och den som börjar i appen gör
          BankID före frågorna. Vill du följa samma personer, använd
          Kohortkonvertering. Ett steg med ”Mäts sedan” visar 0 för dagar före
          det datumet: det är inte mätt, inte ett tapp.
        </p>
      </section>

      {report.dropOffReasons ? (
        <DropOffReasonsSection counts={report.dropOffReasons} />
      ) : null}

      <BankIdPersonsSection bankId={report.bankId} />

      <BankIdMethodSection report={report} />

      <ReminderSection report={report} />

      {report.daily.length > 1 ? (
        <section className="flex flex-col gap-sm">
          <h2 className="text-sm font-medium">Per dag</h2>
          <DailyTable report={report} />
        </section>
      ) : null}
    </div>
  );
}

function Percent({ value }: { value: number | null }) {
  return (
    <span className="text-textSecondary">
      {value === null ? "–" : `${value} %`}
    </span>
  );
}

/**
 * TES-1167 — Emelie's six steps per origin segment (where she STARTED),
 * the same people and window as the cohort above. Exported for the test.
 */
export function SegmentFunnelSection({
  report,
}: {
  report: OnboardingCohortReport;
}) {
  const segments = report.segments ?? [];
  const rows = segmentRows(report);
  const conversions = segmentConversions(report);
  return (
    <section
      className="flex flex-col gap-sm"
      aria-labelledby="funnel-segments-heading"
      data-segment-funnel
    >
      <h2 id="funnel-segments-heading" className="text-sm font-medium">
        Per startkanal – från e-post till BankID i appen
      </h2>
      <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
              <th className="px-md py-sm font-medium">
                Steg inom {report.windowDays} dagar
              </th>
              {segments.map((s) => (
                <th
                  key={s.origin}
                  className="px-md py-sm font-medium text-right"
                  scope="col"
                >
                  {ORIGIN_LABEL[s.origin]} ({fmt.format(s.size)})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.key}
                className="border-t border-surfaceMuted align-top"
                data-segment-step={r.key}
              >
                <td className="px-md py-sm">
                  <div className="flex flex-col gap-xs">
                    <span>{r.label}</span>
                    {r.key === "feedback_visible" ? (
                      <span className="text-xs text-textSecondary">
                        I appen: proxy (hämtning) tills app 2.0.8.
                      </span>
                    ) : null}
                    <MeasuredLine measured={r.measured} />
                  </div>
                </td>
                {r.cells.map((c) => (
                  <td
                    key={c.origin}
                    className="px-md py-sm text-right tabular-nums"
                  >
                    {c.notApplicable ? (
                      <span
                        className="text-textSecondary"
                        title="Resultatet visas direkt i appen"
                      >
                        ej tillämpligt ({fmt.format(c.count)})
                      </span>
                    ) : (
                      <>
                        {fmt.format(c.count)} <Percent value={c.percent} />
                      </>
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr
              className="border-t border-surfaceMuted align-top font-medium"
              data-segment-conversion
            >
              <td className="px-md py-sm">
                E-post verifierad → Personlig återkoppling visad
              </td>
              {conversions.map((c) => (
                <td
                  key={c.origin}
                  className="px-md py-sm text-right tabular-nums"
                >
                  {fmt.format(c.visible)}/{fmt.format(c.verified)}{" "}
                  <Percent value={c.percent} />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <ul className="text-xs text-textSecondary max-w-2xl flex flex-col gap-xs">
        {segments.flatMap((s) =>
          segmentNotes(s).map((n) => (
            <li key={`${s.origin}:${n.key}`}>
              <span className="font-medium">{ORIGIN_LABEL[s.origin]}:</span>{" "}
              {n.text}
            </li>
          )),
        )}
      </ul>
      {/* TES-1202 — step 5 counts only installs someone signed in on. */}
      {report.unlinkedFirstOpens !== undefined ? (
        <p
          className="text-xs text-textSecondary max-w-2xl"
          data-segment-unlinked-installs
        >
          Installationer utan inloggning (granskning/robotar m.m.):{" "}
          {fmt.format(report.unlinkedFirstOpens)}. De ingår inte i steg 5 –
          efter varje butiksuppladdning installerar Apples granskning och Google
          Plays testrobotar appen utan att logga in.
        </p>
      ) : null}
      <p className="text-xs text-textSecondary max-w-2xl">
        Startkanalen är var hon började: webbformuläret eller hennes första
        BankID, och på vilken enhet; i appen, på vilken plattform. Andelen i
        stegen är av alla i kanalen; konverteringen längst ned är av dem som
        verifierade e-posten. ”Visad” på webben är när resultatet laddats klart
        och syntes på skärmen. I appen räknas tills vidare att appen hämtade
        resultatet (proxy) – den riktiga skärmhändelsen kommer med app 2.0.8.
        För den som började i appen kommer steg 5–6 före steg 1, och steg 4
        gäller inte: resultatet visas direkt i appen.
        {report.originSources && report.originSources.length > 0 ? (
          <>
            {" "}
            Startkanalen härleddes från:{" "}
            {report.originSources
              .map(
                (o) =>
                  `${ORIGIN_SOURCE_LABEL[o.source]} ${fmt.format(o.count)}`,
              )
              .join(", ")}
            .
          </>
        ) : null}
      </p>
    </section>
  );
}

/** TES-1096 — the loaded cohort. Exported for the render test. */
// TES-1323 — the cohort's steps per F-01 version, so the introduction of
// "Önskat stöd" (v11) can be measured. Counts only; what she answered is the
// insight view's (Ö-535, TES-1326), not this funnel's.
export function F01VersionSection({
  report,
}: {
  report: OnboardingCohortReport;
}) {
  const rows = f01VersionRows(report);
  const buckets = report.f01Versions ?? [];
  if (rows.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-sm"
      aria-labelledby="funnel-f01-versions-heading"
      data-cohort-f01-versions
    >
      <h2 id="funnel-f01-versions-heading" className="text-sm font-medium">
        Per F-01-version
      </h2>
      <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
              <th className="px-md py-sm font-medium">
                Steg inom {report.windowDays} dagar
              </th>
              {buckets.map((b) => (
                <th
                  key={b.bucket}
                  className="px-md py-sm font-medium text-right"
                  scope="col"
                >
                  {F01_VERSION_BUCKET_LABEL[b.bucket]} ({fmt.format(b.size)})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-surfaceMuted">
                <td className="px-md py-sm">{r.label}</td>
                {r.cells.map((c) => (
                  <td
                    key={c.bucket}
                    className="px-md py-sm text-right tabular-nums"
                  >
                    {fmt.format(c.count)} <Percent value={c.percent} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-textSecondary max-w-2xl">
        Samma kohort uppdelad efter formulärversionen i personens senaste F-01.
        Frågan om önskat stöd finns från v11; den som svarade på en äldre
        version räknas aldrig som att hon inte svarat. Andelen är av alla i
        kolumnen.
      </p>
    </section>
  );
}

export function OnboardingCohortView({
  report,
  actingRole,
  onPeriodChange,
}: {
  report: OnboardingCohortReport;
  /** For the patient links in a journey step's drill-down. */
  actingRole?: string;
  /** Ö-354 — the journey funnel's period choice. */
  onPeriodChange?: (period: JourneyFunnelPeriod) => void;
}) {
  const rows = cohortRows(report);
  const tables = cohortBreakdownTables(report);
  const bankIdRows = bankIdPersonRows(report.bankId);
  const total = report.channels.reduce((n, c) => n + c.size, 0);
  const open = report.channels.reduce((n, c) => n + c.openWindows, 0);
  return (
    <div className="flex flex-col gap-lg">
      {!report.complete ? (
        <div
          className="border border-warning rounded-md p-md bg-surface text-sm flex flex-col gap-xs"
          role="status"
          data-cohort-incomplete
        >
          <p className="font-medium">Kohorten är inte klar</p>
          <p className="text-textSecondary max-w-2xl">
            {fmt.format(open)} av {fmt.format(total)} personer har ännu inte
            haft sina {report.windowDays} dagar. De senare stegen kan
            fortfarande öka; siffrorna är slutliga från{" "}
            {report.windowsCloseAt.slice(0, 10)}.
          </p>
        </div>
      ) : null}

      {total === 0 ? (
        <div className="border border-surfaceMuted rounded-md p-lg bg-surface flex flex-col gap-sm items-start">
          <p className="text-sm font-medium">Ingen började under perioden</p>
          <p className="text-sm text-textSecondary max-w-lg">
            Kohorten är de personer vars första mätbara steg ligger i perioden.
            Testkonton räknas inte.
          </p>
        </div>
      ) : null}

      <section className="flex flex-col gap-sm">
        <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
                <th className="px-md py-sm font-medium">
                  Steg inom {report.windowDays} dagar
                </th>
                {report.channels.map((c) => (
                  <th
                    key={c.channel}
                    className="px-md py-sm font-medium text-right"
                    scope="col"
                  >
                    {COHORT_CHANNEL_LABEL[c.channel]} ({fmt.format(c.size)})
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.key}
                  className="border-t border-surfaceMuted align-top"
                  data-cohort-step={r.key}
                >
                  <td className="px-md py-sm">
                    <div className="flex flex-col gap-xs">
                      <span>{r.label}</span>
                      <MeasuredLine measured={r.measured} />
                    </div>
                  </td>
                  {r.cells.map((c) => (
                    <td
                      key={c.channel}
                      className="px-md py-sm text-right tabular-nums"
                    >
                      {fmt.format(c.count)} <Percent value={c.percent} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-textSecondary max-w-2xl">
          Kohorten är de unika personer vars första mätbara steg – ett
          webbformulär som kopplats till henne, eller hennes första BankID –
          ligger i perioden. Webben: hon svarade på frågorna på webben eller
          gjorde BankID där först; appen: hon började i appen. Varje person
          följs {report.windowDays} dagar från sin egen start, och andelen är av
          alla i kohorten. {fmt.format(report.unlinkedLeads)} webbformulär under
          perioden kopplades aldrig till någon person och ingår inte.
          {report.unlinkedFirstOpens !== undefined && (
            <>
              {" "}
              {fmt.format(report.unlinkedFirstOpens)} appinstallationer öppnades
              första gången under perioden utan att någon loggat in
              (granskning/robotar m.m.) och ingår inte heller.
            </>
          )}
        </p>
      </section>

      {report.segments && report.segments.length > 0 ? (
        <SegmentFunnelSection report={report} />
      ) : null}

      <F01VersionSection report={report} />

      {tables.map((t) => (
        <section
          key={`${t.dimension}:${t.channel}`}
          className="flex flex-col gap-sm"
          data-cohort-breakdown={`${t.dimension}:${t.channel}`}
        >
          <h2 className="text-sm font-medium">{t.title}</h2>
          <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
                  <th className="px-md py-sm font-medium">Grupp</th>
                  <th className="px-sm py-sm font-medium text-right">
                    Personer
                  </th>
                  {(t.rows[0]?.cells ?? []).map((c) => (
                    <th
                      key={c.key}
                      className="px-sm py-sm font-medium text-right"
                      scope="col"
                    >
                      {COHORT_STEP_LABEL[c.key]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.rows.map((row) => (
                  <tr key={row.label} className="border-t border-surfaceMuted">
                    <td className="px-md py-sm whitespace-nowrap">
                      {row.label}
                    </td>
                    <td className="px-sm py-sm text-right tabular-nums">
                      {fmt.format(row.size)}
                    </td>
                    {row.cells.map((c) => (
                      <td
                        key={c.key}
                        className="px-sm py-sm text-right tabular-nums whitespace-nowrap"
                      >
                        {fmt.format(c.count)} <Percent value={c.percent} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      {tables.length > 0 ? (
        <p className="text-xs text-textSecondary max-w-2xl">
          Källa / kampanj läses från webbformulärets UTM-taggar (Instagram när
          källan är ig/instagram). Enhet är den enhet hon gjorde sitt första
          BankID på. Väg till appen är taggen på hennes senaste klick på en
          butiksknapp eller QR-kod innan hon öppnade appen – webbens, mejlets
          eller en Instagram-länks.
        </p>
      ) : null}

      {report.journeyFunnel ? (
        <JourneyFunnelSection
          key={`${report.journeyFunnel.period}:${report.journeyFunnel.journey}`}
          funnel={report.journeyFunnel}
          actingRole={actingRole}
          onPeriodChange={onPeriodChange}
        />
      ) : null}

      {bankIdRows.length > 0 ? (
        <BankIdPersonsSection bankId={report.bankId} />
      ) : null}
    </div>
  );
}

/** TES-1117 S3 — one journey's funnel, counts only. Exported for the test. */
export function JourneyFunnelTable({
  funnel,
  selected,
  onSelect,
}: {
  funnel: JourneyFunnel;
  selected: JourneyFunnelStepKey | null;
  onSelect?: (step: JourneyFunnelStepKey) => void;
}) {
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Steg hittills</th>
            <th className="px-md py-sm font-medium text-right" scope="col">
              Personer ({fmt.format(funnel.size)})
            </th>
            <th className="px-md py-sm font-medium text-right" scope="col">
              {JOURNEY_PROGRESS_LABEL.ongoing} ({fmt.format(funnel.ongoing)})
            </th>
            <th className="px-md py-sm font-medium text-right" scope="col">
              {JOURNEY_PROGRESS_LABEL.dropped_out} (
              {fmt.format(funnel.droppedOut)})
            </th>
            <th className="px-md py-sm font-medium" />
          </tr>
        </thead>
        <tbody>
          {journeyFunnelRows(funnel).map((r) => (
            <tr
              key={r.key}
              className={`border-t border-surfaceMuted ${
                selected === r.key ? "bg-brand/10" : ""
              }`}
              data-journey-step={r.key}
            >
              <td className="px-md py-sm">
                {r.label}
                {r.conditional ? (
                  <span className="block text-xs text-textSecondary">
                    {CONDITIONAL_STEP_NOTE}
                  </span>
                ) : null}
              </td>
              <td className="px-md py-sm text-right tabular-nums">
                {fmt.format(r.count)} <Percent value={r.percent} />
              </td>
              <td className="px-md py-sm text-right tabular-nums" data-ongoing>
                {fmt.format(r.ongoing)}
              </td>
              <td
                className="px-md py-sm text-right tabular-nums"
                data-dropped-out
              >
                {fmt.format(r.droppedOut)}
              </td>
              <td className="px-md py-sm text-right">
                {onSelect && r.count > 0 ? (
                  <button
                    type="button"
                    onClick={() => onSelect(r.key)}
                    aria-pressed={selected === r.key}
                    className="text-brand hover:underline"
                  >
                    Visa patienter
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** TES-1117 S3 — the people at one step. Exported for the test. */
export function JourneyStepPatientsList({
  step,
  rows,
  actingRole,
}: {
  step: JourneyFunnelStepKey;
  rows: JourneyStepPatient[];
  actingRole: string | undefined;
}) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-textSecondary">{emptyJourneyStepText(step)}</p>
    );
  }
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Namn</th>
            <th className="px-md py-sm font-medium">Aktuell status</th>
            <th className="px-md py-sm font-medium">Väntar på</th>
            <th className="px-md py-sm font-medium">Senaste aktivitet</th>
            <th className="px-md py-sm font-medium">Började</th>
            <th className="px-md py-sm font-medium" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.patientId}
              className="border-t border-surfaceMuted align-top"
            >
              <td className="px-md py-sm font-medium">{r.patientName}</td>
              <td className="px-md py-sm">
                {r.status}
                {r.progress !== "finished" ? (
                  <span
                    className="block text-xs text-textSecondary"
                    data-progress={r.progress}
                  >
                    {JOURNEY_PROGRESS_LABEL[r.progress]}
                  </span>
                ) : null}
              </td>
              <td className="px-md py-sm">
                {r.waitingOn ? ONBOARDING_WAITING_ON_LABEL[r.waitingOn] : "—"}
              </td>
              <td className="px-md py-sm tabular-nums">
                {r.latestActivityAt ? r.latestActivityAt.slice(0, 10) : "—"}
              </td>
              <td className="px-md py-sm tabular-nums">
                {r.startedAt.slice(0, 10)}
              </td>
              <td className="px-md py-sm">
                <Link
                  to={adminPatientHref(actingRole, r.patientId)}
                  className="text-brand hover:underline"
                >
                  Patienten
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Ö-354 — "7 dagar / 30 dagar / 90 dagar / Alla". Exported for the test. */
export function JourneyPeriodChoice({
  period,
  onChange,
}: {
  period: JourneyFunnelPeriod;
  onChange?: (period: JourneyFunnelPeriod) => void;
}) {
  return (
    <div className="flex gap-xs" role="group" aria-label="Period">
      {JOURNEY_PERIOD_FILTERS.map((f) => (
        <button
          key={f.key}
          type="button"
          aria-pressed={period === f.key}
          disabled={!onChange}
          onClick={() => onChange?.(f.key)}
          className={`px-md py-sm rounded-md border text-sm ${
            period === f.key
              ? "border-brand bg-brand/10"
              : "border-surfaceMuted"
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

function JourneyFunnelSection({
  funnel,
  actingRole,
  onPeriodChange,
}: {
  funnel: JourneyFunnel;
  actingRole: string | undefined;
  onPeriodChange?: (period: JourneyFunnelPeriod) => void;
}) {
  const { signOut } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [step, setStep] = useState<JourneyFunnelStepKey | null>(null);
  const [rows, setRows] = useState<JourneyStepPatient[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const select = async (next: JourneyFunnelStepKey) => {
    setStep(next);
    setRows(null);
    setError(null);
    try {
      const list = await fetchJourneyStepPatients(
        funnel.period,
        funnel.journey,
        next,
      );
      setRows(list.rows);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <section className="flex flex-col gap-sm" data-journey-funnel>
      <h2 className="text-sm font-medium">
        Hela resan – {JOURNEY_FILTER_LABEL[funnel.journey]}
      </h2>
      <JourneyPeriodChoice period={funnel.period} onChange={onPeriodChange} />
      <JourneyFunnelTable
        funnel={funnel}
        selected={step}
        onSelect={(k) => void select(k)}
      />
      <p className="text-xs text-textSecondary max-w-2xl">
        {journeyPeriodText(funnel.period)} Andelen är av dem.{" "}
        {JOURNEY_PROGRESS_EXPLANATION} ”Visa patienter” listar dem som står vid
        steget just nu och visar namn – internt loggas det som åtkomst till
        varje person i listan.
      </p>
      {step ? (
        error ? (
          <p className="text-sm text-danger" role="alert">
            Patienterna kunde inte hämtas ({error}).
          </p>
        ) : rows ? (
          <JourneyStepPatientsList
            step={step}
            rows={rows}
            actingRole={actingRole}
          />
        ) : (
          <p className="text-sm text-textSecondary">Hämtar…</p>
        )
      ) : null}
    </section>
  );
}

/** Who the journey funnel counts, in the period chosen. */
export function journeyPeriodText(period: JourneyFunnelPeriod): string {
  const days = JOURNEY_FUNNEL_PERIOD_DAYS[period];
  return days === null
    ? "Alla som har påbörjat resan, följda till idag."
    : `De som påbörjade resan de senaste ${days} dagarna, följda till idag.`;
}

const JOURNEY_FILTER_LABEL = Object.fromEntries(
  JOURNEY_FILTERS.filter((f) => f.key).map((f) => [f.key, f.label]),
) as Record<PatientGroup, string>;

const VIEWS = [
  { key: "activity", label: "Aktivitet under perioden" },
  { key: "cohort", label: "Kohortkonvertering" },
] as const;
type ViewKey = (typeof VIEWS)[number]["key"];

export function OnboardingFunnelRoute() {
  const [range, setRange] = useState(() => {
    const today = stockholmToday();
    return { from: today, to: today };
  });
  const [view, setView] = useState<ViewKey>("activity");
  // TES-1117 S3 — all (null), new treatment or transfer.
  const [journey, setJourney] = useState<PatientGroup | null>(null);
  // Ö-354 — the journey funnel's period.
  const [period, setPeriod] = useState<JourneyFunnelPeriod>(
    DEFAULT_JOURNEY_FUNNEL_PERIOD,
  );
  const { claims } = useAuth();
  const [data, setData] = useState<OnboardingFunnelReport | null>(null);
  const [cohort, setCohort] = useState<OnboardingCohortReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invalid = rangeError(range.from, range.to);

  const load = useCallback(async () => {
    if (rangeError(range.from, range.to)) return;
    setError(null);
    setData(null);
    setCohort(null);
    try {
      if (view === "activity") {
        setData(await fetchOnboardingFunnel(range.from, range.to));
      } else {
        setCohort(
          await fetchOnboardingCohort(range.from, range.to, journey, period),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "okänt fel");
    }
  }, [range, view, journey, period]);

  useEffect(() => {
    void load();
  }, [load]);

  const preset = (days: number) =>
    setRange(rangeEndingAt(stockholmToday(), days));
  const activePreset = PRESETS.find(
    (p) =>
      range.to === stockholmToday() &&
      rangeEndingAt(range.to, p.days).from === range.from,
  )?.days;

  const header = (
    <div className="flex items-start justify-between gap-md flex-wrap">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">
          Onboardingtratt
        </h1>
        <p className="text-textSecondary text-sm max-w-xl">
          Från de första frågorna till ett bokat vårdmöte, i svensk tid. Bara
          antal – inga namn eller personnummer, utom när du väljer ”Visa
          patienter” vid ett steg på en resa. Testkonton räknas inte.
        </p>
        <div className="flex gap-xs mt-sm" role="tablist" aria-label="Vy">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={view === v.key}
              onClick={() => setView(v.key)}
              className={`px-md py-sm rounded-md border text-sm ${
                view === v.key
                  ? "border-brand bg-brand/10"
                  : "border-surfaceMuted"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        {view === "cohort" ? (
          <div className="flex gap-xs" role="group" aria-label="Resa">
            {JOURNEY_FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                aria-pressed={journey === f.key}
                onClick={() => setJourney(f.key)}
                className={`px-md py-sm rounded-md border text-sm ${
                  journey === f.key
                    ? "border-brand bg-brand/10"
                    : "border-surfaceMuted"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-sm items-end">
        <div className="flex gap-xs" role="group" aria-label="Snabbval">
          {PRESETS.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => preset(p.days)}
              aria-pressed={activePreset === p.days}
              className={`px-md py-sm rounded-md border text-sm ${
                activePreset === p.days
                  ? "border-brand bg-brand/10"
                  : "border-surfaceMuted"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-sm items-end flex-wrap">
          <label className="flex flex-col gap-xs text-xs text-textSecondary">
            Från
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e) =>
                setRange((r) => ({ ...r, from: e.target.value }))
              }
              className="px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary"
            />
          </label>
          <label className="flex flex-col gap-xs text-xs text-textSecondary">
            Till
            <input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary"
            />
          </label>
        </div>
        {invalid ? (
          <p className="text-xs text-danger" role="alert">
            {invalid}
          </p>
        ) : null}
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-lg">
        {header}
        <div className="border border-danger rounded-md p-md text-sm bg-surface flex flex-col gap-sm items-start">
          <p className="text-danger">Tratten kunde inte hämtas ({error}).</p>
          <button
            type="button"
            onClick={() => void load()}
            className="px-md py-sm rounded-md border border-surfaceMuted"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      {header}
      {view === "activity" && data ? (
        <OnboardingFunnelView report={data} />
      ) : view === "cohort" && cohort ? (
        <OnboardingCohortView
          report={cohort}
          actingRole={claims?.role}
          onPeriodChange={setPeriod}
        />
      ) : invalid ? null : (
        <div
          className="h-96 rounded-md border border-surfaceMuted bg-surfaceMuted/30 animate-pulse"
          aria-busy="true"
        />
      )}
    </div>
  );
}
